import React, { Fragment, MouseEvent } from 'react'
import { PaginationButtonsProps } from './PaginationButtons.types'
import styles from './PaginationButtons.module.scss'
import cl from 'classnames'
import { Text1 } from '../Text1'
import { IconArrowLeft, IconArrowRight } from 'assets'
import { Button } from '../Button'

const preventFocusScroll = (event: MouseEvent<HTMLButtonElement>) => {
  event.preventDefault()
}

const canScrollVertically = (el: HTMLElement) => {
  const overflowY = window.getComputedStyle(el).overflowY
  const canScrollY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
  return canScrollY && el.scrollHeight > el.clientHeight + 1
}

const scrollTableToStart = (fromEl: HTMLElement) => {
  let node: HTMLElement | null = fromEl.parentElement
  let resetCount = 0

  while (node && node !== document.body && resetCount < 3) {
    if (canScrollVertically(node)) {
      node.scrollTop = 0
      resetCount += 1
    }

    Array.from(node.children).forEach(child => {
      if (!(child instanceof HTMLElement) || resetCount >= 3) return
      if (!canScrollVertically(child)) return
      child.scrollTop = 0
      resetCount += 1
    })

    node = node.parentElement
  }
}

const PaginationButtonsComponent = (props: PaginationButtonsProps) => {
  const {
    alwaysShow,
    totalPages,
    selectedPageNumber,
    totalElements,
    elementsPerPage,
    onPageSelect,
    className,
    style
  } = props

  if (totalPages < 2 && !alwaysShow) return null

  const isSelectedPageFirst = selectedPageNumber === 0
  const isSelectedPageLast = selectedPageNumber === totalPages - 1

  const skippedItemsNumber = elementsPerPage * selectedPageNumber

  const shownElementMin = skippedItemsNumber + 1
  const shownElementMax = Math.min(skippedItemsNumber + elementsPerPage, totalElements)

  const selectPage = (pageNum: number, event: MouseEvent<HTMLButtonElement>) => {
    if (pageNum === selectedPageNumber) return
    onPageSelect && onPageSelect(pageNum)
    scrollTableToStart(event.currentTarget)
  }

  return (
    <div className={cl(styles.container, className)} style={style}>
      <Text1 className={styles.infoText}>
        Showing {shownElementMin} to {shownElementMax} of {totalElements} results
      </Text1>

      <div className={styles.buttonsContainer}>

        <Button
          preset='icon'
          className={cl(styles.pageButton)}
          disabled={isSelectedPageFirst}
          onMouseDown={preventFocusScroll}
          onClick={event => selectPage(selectedPageNumber - 1, event)}
        >
          <IconArrowLeft />
        </Button>

        {totalPages <= 5
          ? (
            Array(totalPages).fill('').map((_, index) => {
              return (
                <Button
                  key={index}
                  preset='icon'
                  className={cl(styles.pageButton, {
                    [styles.selected]: index === selectedPageNumber
                  })}
                  onMouseDown={preventFocusScroll}
                  onClick={event => selectPage(index, event)}
                >
                  {index + 1}
                </Button>
              )
            })
          )
          : (
            <Fragment>
              <Button
                preset='icon'
                className={cl(styles.pageButton, {
                  [styles.selected]: selectedPageNumber === 0
                })}
                onMouseDown={preventFocusScroll}
                onClick={event => selectPage(0, event)}
              >
                1
              </Button>

              {selectedPageNumber <= 2 && (
                <Fragment>
                  <Button
                    preset='icon'
                    className={cl(styles.pageButton, {
                      [styles.selected]: selectedPageNumber === 1
                    })}
                    onMouseDown={preventFocusScroll}
                    onClick={event => selectPage(1, event)}
                  >
                    2
                  </Button>
                  <Button
                    preset='icon'
                    className={cl(styles.pageButton, {
                      [styles.selected]: selectedPageNumber === 2
                    })}
                    onMouseDown={preventFocusScroll}
                    onClick={event => selectPage(2, event)}
                  >
                    3
                  </Button>
                </Fragment>
              )}

              <div className={styles.pageButton}>...</div>

              {selectedPageNumber > 2 && selectedPageNumber < totalPages - 3 && (
                <Fragment>
                  <Button
                    preset='icon'
                    className={cl(styles.pageButton, styles.selected)}
                  >
                    {selectedPageNumber + 1}
                  </Button>
                  <div className={styles.pageButton}>...</div>
                </Fragment>
              )}

              {selectedPageNumber >= totalPages - 3 && (
                <Fragment>
                  <Button
                    preset='icon'
                    className={cl(styles.pageButton, {
                      [styles.selected]: selectedPageNumber === totalPages - 3
                    })}
                    onMouseDown={preventFocusScroll}
                    onClick={event => selectPage(totalPages - 3, event)}
                  >
                    {totalPages - 2}
                  </Button>
                  <Button
                    preset='icon'
                    className={cl(styles.pageButton, {
                      [styles.selected]: selectedPageNumber === totalPages - 2
                    })}
                    onMouseDown={preventFocusScroll}
                    onClick={event => selectPage(totalPages - 2, event)}
                  >
                    {totalPages - 1}
                  </Button>
                </Fragment>
              )}

              <Button
                preset='icon'
                className={cl(styles.pageButton, {
                  [styles.selected]: selectedPageNumber === totalPages - 1
                })}
                onMouseDown={preventFocusScroll}
                onClick={event => selectPage(totalPages - 1, event)}
              >
                {totalPages}
              </Button>

            </Fragment>
          )}

        <Button
          preset='icon'
          className={cl(styles.pageButton)}
          disabled={isSelectedPageLast}
          onMouseDown={preventFocusScroll}
          onClick={event => selectPage(selectedPageNumber + 1, event)}
        >
          <IconArrowRight />
        </Button>

      </div>

    </div>
  )
}

const PaginationButtons = PaginationButtonsComponent
export { PaginationButtons }
